"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/motion";
import type { IpInfo } from "@suite/lib/geo";
import { GEO_DATA_ATTRIBUTION } from "@/lib/constants";
import { Card } from "@suite/components/ui/card";
import InfoTooltip from "@suite/components/ui/info-tooltip";
import { AlertCircle, Download, Globe, MapPin, RefreshCw } from "@suite/components/icons";
import { cn, countryToFlag } from "@suite/lib/utils";
import CopyButton from "./CopyButton";

const HEADER = "font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70";

/** Short explanations for terms that mean something more specific than they sound like. */
const DETAIL_TOOLTIPS: Record<string, string> = {
  "ISP / organisation":
    "The autonomous system organisation this address is registered to — the closest field this database has to an ISP name. Proxies, VPNs and mobile networks often show a datacenter or aggregator name here rather than a residential ISP.",
  ASN: "Autonomous System Number — the ID of the network operator that routes this address.",
  "Browser timezone":
    "Your device's own clock, from the browser — not derived from this IP address, which can be geolocated to a different region than the device it belongs to.",
};

interface SelfIp {
  ip: string;
  ipVersion: "v4" | "v6";
}

function isSelfIp(value: unknown): value is SelfIp {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  return typeof r.ip === "string" && (r.ipVersion === "v4" || r.ipVersion === "v6");
}

function isIpInfo(value: unknown): value is IpInfo {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  return typeof r.ip === "string" && (r.ipVersion === "v4" || r.ipVersion === "v6");
}

interface AddressEntry {
  self: SelfIp;
  /** Null when the geolocation lookup failed or the local database is unavailable — the IP
   * address itself is still shown, just without country/city/ISP/ASN. Never fabricated. */
  geo: IpInfo | null;
}

interface Report {
  addresses: AddressEntry[];
  /** True when this deployment is configured to check both address families (two family-pinned
   * hostnames set), so the UI can explain a missing family honestly rather than looking broken. */
  dualStackChecked: boolean;
}

/**
 * Family-pinned hostnames — one resolving over IPv4 only, one over IPv6 only — that let this page
 * force a request over a specific address family instead of whichever one the OS resolver and
 * Happy Eyeballs would otherwise pick for a single hostname. Same two env vars the WebRTC Leak
 * Test already uses for its cross-family checks, so configuring them once benefits both tools.
 * Each value is the full endpoint URL (e.g. "https://ipv4.proxidize.com/api/tools/ip/"), not just
 * an origin. Unset in this deployment: falls back to the single default-origin address below.
 */
const IPV4_ECHO_URL = process.env.NEXT_PUBLIC_IPV4_ECHO_URL;
const IPV6_ECHO_URL = process.env.NEXT_PUBLIC_IPV6_ECHO_URL;
const DUAL_STACK_CONFIGURED = Boolean(IPV4_ECHO_URL || IPV6_ECHO_URL);

/** Bounded so a hung request degrades to an honest failure instead of stalling the page. */
const FETCH_TIMEOUT_MS = 8000;

async function fetchJson<T>(url: string, guard: (value: unknown) => value is T): Promise<T | null> {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const value: unknown = await response.json();
    return guard(value) ? value : null;
  } catch {
    return null;
  }
}

/** "Sydney, New South Wales, Australia" — from whichever of city/region/country the database
 * actually returned, never inventing a placeholder for a field it didn't have. */
function formatLocation(geo: IpInfo): string {
  return [geo.city, geo.region, geo.country].filter(Boolean).join(", ");
}

function reportToText(report: Report, timezone: string): string {
  const lines: string[] = [];
  for (const { self, geo } of report.addresses) {
    lines.push(
      `IP address: ${self.ip}`,
      `IP version: ${self.ipVersion === "v6" ? "IPv6" : "IPv4"}`
    );
    if (geo) {
      lines.push(
        `City: ${geo.city ?? "unavailable"}`,
        `Region: ${geo.region ?? "unavailable"}`,
        `Country: ${geo.country ?? "unavailable"}`,
        `Continent: ${geo.continent ?? "unavailable"}`,
        `ISP / organisation: ${geo.isp ?? "unavailable"}`,
        `ASN: ${geo.asn ?? "unavailable"}`
      );
    } else {
      lines.push("Location: unavailable — the IP geolocation database couldn't be reached.");
    }
    lines.push("");
  }
  lines.push(`Browser timezone: ${timezone}`, "");
  if (report.addresses.some((a) => a.geo)) {
    lines.push(
      `${GEO_DATA_ATTRIBUTION.text} (${GEO_DATA_ATTRIBUTION.href}), licensed ${GEO_DATA_ATTRIBUTION.licenseText} (${GEO_DATA_ATTRIBUTION.licenseHref})`
    );
  }
  return lines.join("\n").trim();
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  const tooltip = DETAIL_TOOLTIPS[label];
  return (
    <div className="-mx-1.5 flex items-start justify-between gap-3 rounded border-b border-white/[0.04] px-1.5 py-2 transition-colors last:border-0 hover:bg-white/[0.025]">
      <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold tracking-widest text-white/70 uppercase">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </span>
      <span className="min-w-0 text-end font-mono text-[13px] [overflow-wrap:anywhere] break-words text-white/85">
        {value}
      </span>
    </div>
  );
}

interface AddressCardProps {
  entry: AddressEntry;
  busy: boolean;
  reduced: boolean;
  onRefresh: () => void;
  /** Only the first card shows the Refresh control — one action re-checks every address. */
  showRefresh: boolean;
}

function AddressCard({ entry, busy, reduced, onRefresh, showRefresh }: AddressCardProps) {
  const { self, geo } = entry;
  const flag = geo?.countryCode ? countryToFlag(geo.countryCode) : null;
  const location = geo ? formatLocation(geo) : "";

  return (
    <Card className="@container relative gap-0 overflow-hidden p-0">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
      </div>
      <div className="relative flex flex-col gap-3 p-5 @4xl:py-7">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            <span className="bg-brand-green/10 text-brand-green flex size-8 shrink-0 items-center justify-center rounded-md">
              <Globe className="size-4" aria-hidden />
            </span>
            <h2
              className={cn(
                "min-w-0 font-mono font-semibold tracking-tight [overflow-wrap:anywhere] break-all text-white",
                // IPv6 literals run up to 4x longer than IPv4 — a fixed 28px size that fits
                // "255.255.255.255" clips or overflows on mobile and at higher browser zoom, so
                // IPv6 gets a smaller scale instead of relying on wrapping alone to save it.
                self.ipVersion === "v6" ? "text-base sm:text-xl" : "text-2xl sm:text-[28px]"
              )}
            >
              {self.ip}
            </h2>
            <span className="rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-[10px] font-semibold tracking-widest text-white/70 uppercase">
              {self.ipVersion === "v6" ? "IPv6" : "IPv4"}
            </span>
            <CopyButton value={self.ip} label="IP address" />
          </div>
          {showRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={busy}
              className="focus-visible:ring-brand-green/70 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2 disabled:opacity-50"
            >
              <RefreshCw
                className={cn("size-3.5", busy && !reduced && "animate-spin")}
                aria-hidden
              />
              Refresh
            </button>
          )}
        </div>
        {geo ? (
          <p className="text-sm leading-relaxed text-white/85">
            {flag && <span className="mr-1.5">{flag}</span>}
            {location || "Unknown location"}
            {geo.isp && <span className="text-white/65"> · {geo.isp}</span>}
          </p>
        ) : (
          <p className="text-sm leading-relaxed text-amber-300/85">
            Location unavailable — the IP geolocation database couldn&apos;t be reached. The address
            above is still accurate.
          </p>
        )}
      </div>
    </Card>
  );
}

export default function IpChecker() {
  const [report, setReport] = useState<Report | null>(null);
  const [failed, setFailed] = useState<"self" | null>(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const running = useRef(false);
  const reduced = useReducedMotion() ?? false;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    // `busy` alone can't stop a double click or Strict Mode's effect replay from launching a
    // second, overlapping fetch pair.
    if (running.current) return;
    running.current = true;
    if (mounted.current) {
      setBusy(true);
      setFailed(null);
    }
    try {
      let selves: SelfIp[];
      if (DUAL_STACK_CONFIGURED) {
        const [v4, v6] = await Promise.all([
          IPV4_ECHO_URL ? fetchJson(IPV4_ECHO_URL, isSelfIp) : Promise.resolve(null),
          IPV6_ECHO_URL ? fetchJson(IPV6_ECHO_URL, isSelfIp) : Promise.resolve(null),
        ]);
        selves = [v4, v6].filter((s): s is SelfIp => s !== null);
      } else {
        const only = await fetchJson("/api/tools/ip/", isSelfIp);
        selves = only ? [only] : [];
      }
      if (!mounted.current) return;
      if (selves.length === 0) {
        setFailed("self");
        return;
      }
      // Same-origin fetch from our own page — this is exactly the case the endpoint's `?ip=`
      // gate is built for, distinct from a bare script hitting it directly. The lookup always
      // goes through the default origin regardless of which family-pinned host reported the
      // address, since the lookup is a plain database read, not a family-sensitive network probe.
      const geos = await Promise.all(
        selves.map((s) => fetchJson(`/api/tools/ip/?ip=${encodeURIComponent(s.ip)}`, isIpInfo))
      );
      if (!mounted.current) return;
      setReport({
        addresses: selves.map((self, i) => ({ self, geo: geos[i] })),
        dualStackChecked: DUAL_STACK_CONFIGURED,
      });
    } finally {
      if (mounted.current) setBusy(false);
      running.current = false;
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const shell = (announcement: string, children: React.ReactNode) => (
    <div className="ph-no-capture flex flex-col gap-5">
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {children}
    </div>
  );

  if (failed === "self") {
    return shell(
      "Couldn't detect your public IP address.",
      <Card className="px-5">
        <div className="flex flex-wrap items-center gap-2.5 text-sm text-white/75">
          <AlertCircle className="size-4 shrink-0 text-amber-300" aria-hidden />
          Couldn&apos;t detect your public IP address. This can happen if you&apos;re offline or a
          network filter blocked the request.
          <button
            type="button"
            onClick={() => run()}
            className="focus-visible:ring-brand-green/70 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
          >
            <RefreshCw className="size-3.5" aria-hidden /> Try again
          </button>
        </div>
      </Card>
    );
  }

  if (!report) {
    return shell(
      "Detecting your public IP address.",
      <Card className="@container relative gap-0 overflow-hidden p-0">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
        </div>
        <div className="relative flex flex-col gap-2 p-5 @4xl:py-7">
          <div className="skeleton-bar h-7 w-72 max-w-full rounded-full" />
          <div className="skeleton-bar mt-2 h-4 w-full max-w-xl rounded-full" />
        </div>
      </Card>
    );
  }

  const { addresses } = report;
  // The device's own clock, not derived from the IP lookup — DB-IP's free City edition carries
  // no time_zone field, and this tool never fabricates a field the database doesn't have. Shown
  // and labelled as a browser value so it's never mistaken for IP-based geolocation.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const missingFamily =
    report.dualStackChecked && addresses.length === 1
      ? addresses[0].self.ipVersion === "v6"
        ? "IPv4"
        : "IPv6"
      : null;
  const anyGeo = addresses.some((a) => a.geo);

  const announcement = addresses
    .map(
      ({ self, geo }) =>
        `${self.ipVersion === "v6" ? "IPv6" : "IPv4"} address ${self.ip}. ${
          geo
            ? `${formatLocation(geo) || "Location unavailable"}${geo.isp ? `, via ${geo.isp}` : ""}.`
            : "Location unavailable."
        }`
    )
    .join(" ");

  return shell(
    announcement,
    <div className="flex flex-col gap-5">
      {addresses.map((entry, i) => (
        <AddressCard
          key={entry.self.ip}
          entry={entry}
          busy={busy}
          reduced={reduced}
          onRefresh={() => run()}
          showRefresh={i === 0}
        />
      ))}

      {missingFamily && (
        <p className="flex items-center gap-1.5 text-[13px] text-white/55">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden />
          No {missingFamily} address was reachable from this connection — that&apos;s expected on a{" "}
          {missingFamily}-only network, not a fault of this tool.
        </p>
      )}

      <Card className="gap-4 px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>Details</span>
          <div className="flex items-center gap-1.5">
            <CopyButton value={reportToText(report, timezone)} label="full report" />
            <button
              type="button"
              onClick={() => {
                const blob = new Blob([JSON.stringify({ ...report, timezone }, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "ip-checker.json";
                a.hidden = true;
                document.body.append(a);
                a.click();
                a.remove();
                // Safari may not consume the object URL until after the click task has returned.
                setTimeout(() => URL.revokeObjectURL(url), 0);
              }}
              aria-label="Download as JSON"
              className="focus-visible:ring-brand-green/70 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
            >
              <Download className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
        {addresses.map(({ self, geo }) => (
          <div
            key={self.ip}
            className="[&:not(:first-child)]:border-t [&:not(:first-child)]:border-white/[0.06] [&:not(:first-child)]:pt-3"
          >
            {addresses.length > 1 && (
              <p className="mb-1 font-mono text-[10px] font-semibold tracking-widest text-white/55 uppercase">
                {self.ipVersion === "v6" ? "IPv6" : "IPv4"}
              </p>
            )}
            <DetailRow label="IP address" value={self.ip} />
            <DetailRow label="IP version" value={self.ipVersion === "v6" ? "IPv6" : "IPv4"} />
            <DetailRow label="City" value={geo?.city ?? "unavailable"} />
            <DetailRow label="Region" value={geo?.region ?? "unavailable"} />
            <DetailRow label="Country" value={geo?.country ?? "unavailable"} />
            <DetailRow label="Continent" value={geo?.continent ?? "unavailable"} />
            <DetailRow label="ISP / organisation" value={geo?.isp ?? "unavailable"} />
            <DetailRow label="ASN" value={geo?.asn ?? "unavailable"} />
          </div>
        ))}
        <div className="border-t border-white/[0.06] pt-3">
          <DetailRow label="Browser timezone" value={timezone} />
        </div>
      </Card>

      {anyGeo && (
        <p className="flex items-center gap-1.5 text-[11px] text-white/45">
          <MapPin className="size-3 shrink-0" aria-hidden />
          <a
            href={GEO_DATA_ATTRIBUTION.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="hover:text-white/70"
          >
            {GEO_DATA_ATTRIBUTION.text}
          </a>
          , licensed{" "}
          <a
            href={GEO_DATA_ATTRIBUTION.licenseHref}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="hover:text-white/70"
          >
            {GEO_DATA_ATTRIBUTION.licenseText}
          </a>
        </p>
      )}
    </div>
  );
}
